import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import classnames from 'classnames';
import { Popover, PopoverHeader } from 'reactstrap';

// Components
import StarIcon from '../StarIcon';
import { ButtonNoStyles } from '@styled';

// Functionality
import useAuthorized from '@hooks/useAuthorized';
import useClickOutside from '@hooks/useClickOutside';
import { useScreenMinMd } from '@hooks/useResponsive';
import { getLoginPath } from '../../../../icsc-app/js/helpers/url.helpers';

const Container = styled.div`
    display: inline-block;
    line-height: 0.8;

    .tooltip {
        filter: drop-shadow(0px 0px 16px rgba(87, 87, 87, 0.2));
        padding: 8px 16px;
        white-space: nowrap;

        &:after {
            border-left: 10px solid transparent !important;
            border-right: 10px solid transparent !important;
            border-top-width: 10px !important;
            bottom: -10px !important;
        }
    }
`;

const randomIntFromInterval = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);
const getPopoverPlacement = ({ isDesktop, tooltipPlacement, tooltipPlacementMobile }) => {
    let placement;
    // from props
    if (isDesktop && tooltipPlacement) {
        placement = tooltipPlacement;
    }
    if (!isDesktop && tooltipPlacementMobile) {
        placement = tooltipPlacementMobile;
    }
    return placement;
};

const getClickOutsideException = target => $('.popover')[0] && !$.contains($('.popover')[0], $(target)[0]);

const FavoriteIcon = ({
    id,
    type,
    tooltipPlacement = 'bottom',
    tooltipPlacementMobile = 'left',
    tooltipLabel,
    isFavorite,
    isLoading = false,
    onClick = () => {},
}) => {
    const { isLoggedIn } = useAuthorized();
    const isDesktop = useScreenMinMd();
    const clickOutsideRef = useRef(null);
    const [popoverOpen, setPopoverOpen] = useState(false);

    useClickOutside({
        ref: clickOutsideRef,
        onClickOutside: () => setPopoverOpen(false),
        exceptionCb: getClickOutsideException,
    });

    useEffect(() => {
        ReactTooltip.hide(clickOutsideRef.current);
    }, [isFavorite]);

    const popoverId = useMemo(() => `tooltip-${randomIntFromInterval(1, 1000)}`, []);
    const placement = useMemo(() => getPopoverPlacement({ isDesktop, tooltipPlacement, tooltipPlacementMobile }), [
        isDesktop,
        tooltipPlacement,
        tooltipPlacementMobile,
    ]);

    // this is not working because of the disabled attr, but trying to prevent extra
    // clicks
    const handleKeyDown = e => {
        if (e.keyCode === 13) {
            handleOnClick();
        }
    };

    const handleOnClick = () => {
        if (onClick && !isLoading) {
            onClick({ id, type });
        }
    };

    const toggle = () => {
        setPopoverOpen(!popoverOpen);
    };

    return (
        <Container>
            {isLoggedIn && (
                <>
                    <ButtonNoStyles
                        ref={clickOutsideRef}
                        id={popoverId}
                        data-tip
                        data-for={popoverId}
                        type="button"
                        tabIndex={0}
                        className={classnames({
                            active: isFavorite,
                            'animate-rotate': isLoading,
                            favorite: !isFavorite, // for analytics purposes
                        })}
                        onClick={handleOnClick}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                    >
                        <span className="sr-only">{isFavorite ? 'Unfavorite' : 'Favorite'}</span>
                        <StarIcon active={isFavorite} />
                    </ButtonNoStyles>

                    <ReactTooltip id={popoverId} className="tooltip" type="light" effect="solid">
                        <span>
                            {isFavorite ? 'Unsave' : 'Save'}
                            {tooltipLabel ? ` ${tooltipLabel}` : ''}
                        </span>
                    </ReactTooltip>
                </>
            )}

            {!isLoggedIn && (
                <>
                    <div>
                        <Popover
                            trigger="click"
                            placement={placement}
                            target={popoverId}
                            isOpen={popoverOpen}
                            toggle={toggle}
                            popperClassName="popover-react"
                        >
                            <PopoverHeader>
                                <div className="bg-gray-dark p-2">
                                    <a href={getLoginPath()} target="_self" className="light">
                                        Log In to Save
                                    </a>
                                </div>
                            </PopoverHeader>
                        </Popover>

                        <ButtonNoStyles ref={clickOutsideRef} id={popoverId} type="button">
                            <span style={{ position: 'absolute', visibility: 'hidden' }}>Log In to Save</span>
                            <StarIcon />
                        </ButtonNoStyles>
                    </div>
                </>
            )}
        </Container>
    );
};

FavoriteIcon.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isFavorite: PropTypes.bool,
    isLoading: PropTypes.bool,
    tooltipLabel: PropTypes.string,

    // article, company, event, member, press, publication, session
    type: PropTypes.string,
    tooltipPlacement: PropTypes.string,
    tooltipPlacementMobile: PropTypes.string,
    onClick: PropTypes.func,
};

export default FavoriteIcon;
